import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './shared/components/layout/Layout';
import FeedPage from './features/jobs/pages/FeedPage';
import JobDetailsPage from './features/jobs/pages/JobDetailsPage';
import LoginPage from './features/auth/pages/LoginPage';
import ForgotPasswordPage from './features/auth/pages/ForgotPasswordPage';
import SignupPage from './features/auth/pages/SignupPage';
import ProtectedRoute from './features/auth/components/ProtectedRoute';
import DashboardShell from './layouts/DashboardShell';
import WorkerLayout from './dashboards/worker/WorkerLayout';
import WorkerHome from './dashboards/worker/pages/Home';
import FindJobs from './dashboards/worker/pages/FindJobs';
import WorkerApplications from './dashboards/worker/pages/Applications';
import WorkerHiredJobs from './dashboards/worker/pages/HiredJobs';
import WorkerCompletedJobs from './dashboards/worker/pages/CompletedJobs';
import WorkerJobDetails from './dashboards/worker/pages/WorkerJobDetails';
import SavedJobs from './dashboards/worker/pages/SavedJobs';
import WorkerMessages from './dashboards/worker/pages/Messages';
import WorkerProfile from './dashboards/worker/pages/Profile';
import WorkerSettingsPage from './dashboards/worker/pages/WorkerSettingsPage';
import ClientDashboardPage from './dashboards/client/ClientDashboardPage';
import { clientSideLinks } from './dashboards/client/clientRoutes';
import ClientJobsPage from './dashboards/client/pages/ClientJobsPage';
import HiredJobsPage from './dashboards/client/pages/HiredJobsPage';
import CompletedJobsPage from './dashboards/client/pages/CompletedJobsPage';
import CompletedJobDetailsPage from './dashboards/client/pages/CompletedJobDetailsPage';
import ClientApplicationsPage from './dashboards/client/pages/ClientApplicationsPage';
import ClientPaymentsPage from './dashboards/client/pages/ClientPaymentsPage';
import ClientSettingsPage from './dashboards/client/pages/ClientSettingsPage';
import ClientProfilePage from './dashboards/client/pages/ClientProfilePage';
import ClientMessagesPage from './dashboards/client/pages/ClientMessagesPage';
import ClientPostJobPage from './dashboards/client/pages/ClientPostJobPage';
import ExpiredJobsPage from './dashboards/client/pages/ExpiredJobsPage';
import ExpiredJobDetailsPage from './dashboards/client/pages/ExpiredJobDetailsPage';
import AdminDashboardPage from './dashboards/admin/AdminDashboardPage';
import AdminUsersPage from './dashboards/admin/pages/AdminUsersPage';
import AdminJobsPage from './dashboards/admin/pages/AdminJobsPage';
import AdminPaymentsPage from './dashboards/admin/pages/AdminPaymentsPage';
import AdminVerificationsPage from './dashboards/admin/pages/AdminVerificationsPage';
import AdminAnalyticsPage from './dashboards/admin/pages/AdminAnalyticsPage';
import AdminLogsPage from './dashboards/admin/pages/AdminLogsPage';
import AdminSettingsPage from './dashboards/admin/pages/AdminSettingsPage';
import AdminReportsPage from './dashboards/admin/pages/AdminReportsPage';
import AdminProfilePage from './dashboards/admin/pages/AdminProfilePage';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  return null;
}

function App() {
  return (
    <Layout>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<FeedPage />} />
        <Route
          path="/post-job"
          element={
            <ProtectedRoute allowedRoles={['CLIENT']}>
              <ClientPostJobPage />
            </ProtectedRoute>
          }
        />
        <Route path="/job/:id" element={<JobDetailsPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route
          path="/worker"
          element={
            <ProtectedRoute allowedRoles={['WORKER']}>
              <WorkerLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<WorkerHome />} />
          <Route path="job/:id" element={<JobDetailsPage />} />
          <Route path="find-jobs" element={<FindJobs />} />
          <Route path="applications" element={<WorkerApplications />} />
          <Route path="hired" element={<WorkerHiredJobs />} />
          <Route path="hired/:applicationId" element={<WorkerJobDetails />} />
          <Route path="completed" element={<WorkerCompletedJobs />} />
          <Route path="completed/:applicationId" element={<WorkerJobDetails completed />} />
          <Route path="messages" element={<WorkerMessages />} />
          <Route path="profile" element={<WorkerProfile />} />
          <Route path="saved" element={<SavedJobs />} />
          <Route path="settings" element={<WorkerSettingsPage />} />
          <Route path="activity" element={<WorkerHome />} />
          <Route path="jobs" element={<WorkerHome />} />
        </Route>
        <Route
          path="/client"
          element={
            <ProtectedRoute allowedRoles={['CLIENT']}>
              <ClientDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/messages"
          element={
            <ProtectedRoute allowedRoles={['CLIENT']}>
              <ClientMessagesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/post-job"
          element={
            <ProtectedRoute allowedRoles={['CLIENT']}>
              <ClientPostJobPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/all-jobs"
          element={
            <ProtectedRoute allowedRoles={['CLIENT']}>
              <DashboardShell role="CLIENT" sideLinks={clientSideLinks}>
                <FeedPage clientMode />
              </DashboardShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/jobs"
          element={
            <ProtectedRoute allowedRoles={['CLIENT']}>
              <ClientJobsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/job/:id"
          element={
            <ProtectedRoute allowedRoles={['CLIENT']}>
              <DashboardShell role="CLIENT" sideLinks={clientSideLinks}>
                <JobDetailsPage />
              </DashboardShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/expired-jobs"
          element={
            <ProtectedRoute allowedRoles={['CLIENT']}>
              <ExpiredJobsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/expired-jobs/:id"
          element={
            <ProtectedRoute allowedRoles={['CLIENT']}>
              <ExpiredJobDetailsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/applications"
          element={
            <ProtectedRoute allowedRoles={['CLIENT']}>
              <ClientApplicationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/hired-jobs"
          element={
            <ProtectedRoute allowedRoles={['CLIENT']}>
              <HiredJobsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/completed-jobs"
          element={
            <ProtectedRoute allowedRoles={['CLIENT']}>
              <CompletedJobsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/completed-jobs/:id"
          element={
            <ProtectedRoute allowedRoles={['CLIENT']}>
              <CompletedJobDetailsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/payments"
          element={
            <ProtectedRoute allowedRoles={['CLIENT']}>
              <ClientPaymentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/settings"
          element={
            <ProtectedRoute allowedRoles={['CLIENT']}>
              <ClientSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/profile"
          element={
            <ProtectedRoute allowedRoles={['CLIENT']}>
              <ClientProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminUsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/jobs"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminJobsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/payments"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminPaymentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/verifications"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminVerificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminAnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/logs"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminLogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/profile"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminProfilePage />
            </ProtectedRoute>
          }
        />
      </Routes>
      <ToastContainer position="bottom-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
    </Layout>
  );
}

export default App;
